import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { GeoPoint, Timestamp } from "firebase/firestore";
import { CalendarDays, Settings2 } from "lucide-react";
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle, DialogTrigger 
} from "@/components/ui/dialog";
import { toast } from "sonner";

import { useCollection } from "@/hooks/firestore";
import { 
  useAddDocument, 
  useUpdateDocument, 
  // useDeleteDocument // No longer used directly
} from "@/hooks/firestore/operations";
import { usePermissions } from "@/hooks/usePermissions";
import { FirestoreLocation, LocationType } from "@/types/firestore";
import { ScrollableTable, Column, SortConfig } from "@/components/ui/scrollable-table";

// Redux imports for deletion dialog
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { DeleteDialog } from "../editor/dialogs/DeleteConfirmDialog";
import { 
    EditableEntityType,
    startDeletionAssessment, 
    assessDeletionImpact, 
    deleteEntity, 
    cancelInteraction,
    selectEditorInteractionState,
    selectSelectedEntity,
    selectDeletionImpact,
    selectEditorOperationLoading,
    selectEditorOperationError
} from "@/store/slices/editorSlice";

export const LocationManager = () => {
  const dispatch = useAppDispatch();
  const { documents: locations, loading: collectionLoading } = useCollection<FirestoreLocation>('locations');
  
  const { addDocument, loading: adding, error: addError } = useAddDocument<FirestoreLocation>('locations');
  const { updateDocument, loading: updating, error: updateError } = useUpdateDocument<FirestoreLocation>('locations');
  // const { deleteDocument, loading: deleting, error: deleteError } = useDeleteDocument('locations'); // Replaced

  // Editor slice selectors
  const interactionState = useAppSelector(selectEditorInteractionState);
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const deletionImpact = useAppSelector(selectDeletionImpact);
  const operationLoading = useAppSelector(selectEditorOperationLoading);
  const operationError = useAppSelector(selectEditorOperationError);

  const { hasPermission } = usePermissions();
  const canManage = hasPermission('editor') || hasPermission('admin');
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<FirestoreLocation | null>(null);
  const [newLocation, setNewLocation] = useState<Partial<FirestoreLocation>>({ 
    name: "",
    address: "",
    locationType: "other",
    coordinates: new GeoPoint(0, 0),
    url: "",
    googlePlaceId: ""
  });
  const [filterLocationType, setFilterLocationType] = useState<LocationType | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<keyof FirestoreLocation | null>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  interface ColumnVisibilityState {
    name: boolean;
    address: boolean;
    locationType: boolean;
    coordinates: boolean;
    averageRating: boolean;
    ratingCount: boolean;
    drinkCount: boolean;
    lastSightingDate: boolean;
    googlePlaceId: boolean;
  }

  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>({
    name: true,
    address: true,
    locationType: true,
    coordinates: true,
    averageRating: false,
    ratingCount: false,
    drinkCount: false,
    lastSightingDate: true,
    googlePlaceId: false,
  });

  // Filter and sort locations
  const processedLocations = useMemo(() => {
    if (!locations) return [];
    let filtered = locations;

    // Filter by locationType
    if (filterLocationType !== null) {
      filtered = filtered.filter((loc: FirestoreLocation) => loc.locationType === filterLocationType);
    }

    // Filter by searchTerm (name or address)
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((loc: FirestoreLocation) => 
        loc.name.toLowerCase().includes(lowerSearchTerm) ||
        loc.address.toLowerCase().includes(lowerSearchTerm)
      );
    }

    // Sort
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];

        // Handle undefined/null for robust sorting, coercing to empty string for comparison if necessary
        if (aValue === undefined || aValue === null) aValue = '' as any;
        if (bValue === undefined || bValue === null) bValue = '' as any;

        if (sortColumn === 'coordinates') { // Special handling for GeoPoint
            // Ensure coordinates exist before trying to access latitude/longitude
            const aLat = (a.coordinates as GeoPoint)?.latitude ?? 0;
            const aLng = (a.coordinates as GeoPoint)?.longitude ?? 0;
            const bLat = (b.coordinates as GeoPoint)?.latitude ?? 0;
            const bLng = (b.coordinates as GeoPoint)?.longitude ?? 0;

            const aCoordString = `${aLat},${aLng}`;
            const bCoordString = `${bLat},${bLng}`;
            return sortDirection === 'asc' 
                ? aCoordString.localeCompare(bCoordString) 
                : bCoordString.localeCompare(aCoordString);
        }

        if (sortColumn === 'lastSightingDate') { // Special handling for Timestamp
          const aDate = (aValue as Timestamp)?.toDate() || new Date(0);
          const bDate = (bValue as Timestamp)?.toDate() || new Date(0);
          return sortDirection === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        // Basic numeric comparison (assuming other sortable fields could be numbers)
        if (typeof aValue === 'number' && typeof bValue === 'number') {
             return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }

        // Fallback for other types or mixed types - convert to string for comparison
        const stringA = String(aValue);
        const stringB = String(bValue);
        return sortDirection === 'asc' 
            ? stringA.localeCompare(stringB) 
            : stringB.localeCompare(stringA);
      });
    } else {
      // Default sort by name
      filtered = [...filtered].sort((a,b) => a.name.localeCompare(b.name));
    }

    return filtered;
  }, [locations, filterLocationType, searchTerm, sortColumn, sortDirection]);
  
  const handleSort = (column: keyof FirestoreLocation) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleAddLocation = async (): Promise<void> => {
    if (!newLocation.name || !newLocation.address) {
      toast.error("Name and address are required");
      return;
    }

    try {
      // Create a complete location object with defaults for required fields
      const locationToAdd: Omit<FirestoreLocation, 'id'> = {
        name: newLocation.name,
        address: newLocation.address,
        locationType: newLocation.locationType || "other",
        coordinates: newLocation.coordinates || new GeoPoint(0, 0),
        url: newLocation.url || "",
        googlePlaceId: newLocation.googlePlaceId || "",
        averageRating: 0,
        ratingCount: 0,
        drinkCount: 0,
        lastSightingDate: Timestamp.now(),
        createdAt: Timestamp.now(), // Include createdAt
        updatedAt: Timestamp.now() // Include updatedAt
      };
      
      const newId = await addDocument(locationToAdd);
      if (newId) {
        setNewLocation({ 
          name: "",
          address: "",
          locationType: "other",
          coordinates: new GeoPoint(0, 0),
          url: "",
          googlePlaceId: ""
        });
        setIsAddDialogOpen(false);
        toast.success("Location added successfully");
      } else {
         toast.error(`Failed to add location: ${addError?.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error("Failed to add location");
      console.error("Add Location Error:", error);
    }
  };

  const handleEditLocation = async (): Promise<void> => {
    if (!editingLocation || !editingLocation.id) return;
    
    try {
      const success = await updateDocument(editingLocation.id, {
        name: editingLocation.name,
        address: editingLocation.address,
        locationType: editingLocation.locationType,
        coordinates: editingLocation.coordinates,
        url: editingLocation.url,
        googlePlaceId: editingLocation.googlePlaceId || "",
        averageRating: editingLocation.averageRating,
        ratingCount: editingLocation.ratingCount,
        drinkCount: editingLocation.drinkCount,
        lastSightingDate: editingLocation.lastSightingDate
        // Note: updatedAt will be added by the hook
      });
      
      if (success) {
        setEditingLocation(null);
        toast.success("Location updated successfully");
      } else {
        toast.error(`Failed to update location: ${updateError?.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error("Failed to update location");
      console.error("Update Location Error:", error);
    }
  };

  const handleDeleteLocationAction = useCallback((location: FirestoreLocation) => {
    if (!location || !location.id) return;
    dispatch(startDeletionAssessment({ entityType: 'location', entityId: location.id }));
    dispatch(assessDeletionImpact({ entityType: 'location', entityId: location.id }));
  }, [dispatch]);

  const handleDeletionDialogClose = useCallback(() => {
    dispatch(cancelInteraction());
  }, [dispatch]);

  const handleConfirmLocationDeletion = useCallback(async () => {
    if (selectedEntity && selectedEntity.type === 'location' && selectedEntity.id) {
      try {
        await dispatch(deleteEntity({ entityType: 'location', entityId: selectedEntity.id })).unwrap();
        
        if (editingLocation?.id === selectedEntity.id) {
          setEditingLocation(null);
        }
      } catch (error: any) {
        // Error handling is now done by the dialog component
        throw error;
      }
    }
  }, [dispatch, selectedEntity, editingLocation]);

  const getDeletionAssessmentState = useCallback(() => {
    if (deletionImpact.error) return 'error';
    if (deletionImpact.loading === 'pending') return 'loading';
    return 'completed';
  }, [deletionImpact.loading, deletionImpact.error]);

  const generalLoading = collectionLoading || adding || updating || (operationLoading === 'pending' && interactionState === 'confirming_deletion');

  // Create table columns configuration
  const tableColumns = useMemo(() => {
    const columns: Column<FirestoreLocation>[] = [];
    
    if (columnVisibility.name) {
      columns.push({
        id: 'name',
        header: 'Name',
        cell: (location) => editingLocation?.id === location.id ? (
          <Input
            value={editingLocation?.name || ""}
            onChange={(e) => setEditingLocation({...editingLocation!, name: e.target.value})}
            disabled={!canManage}
          />
        ) : location.name,
        sortable: true,
        minWidth: '200px'
      });
    }
    
    if (columnVisibility.address) {
      columns.push({
        id: 'address',
        header: 'Address',
        cell: (location) => editingLocation?.id === location.id ? (
          <Input
            value={editingLocation?.address || ""}
            onChange={(e) => setEditingLocation({...editingLocation!, address: e.target.value})}
            disabled={!canManage}
          />
        ) : location.address,
        sortable: true,
        minWidth: '250px'
      });
    }
    
    if (columnVisibility.locationType) {
      columns.push({
        id: 'locationType',
        header: 'Type',
        cell: (location) => editingLocation?.id === location.id ? (
          <Select 
            value={editingLocation?.locationType || 'other'} 
            onValueChange={(value) => {
              setEditingLocation({...editingLocation!, locationType: value as LocationType});
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="restaurant">Restaurant</SelectItem>
              <SelectItem value="shop">Shop</SelectItem>
              <SelectItem value="brewery">Brewery</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        ) : location.locationType,
        sortable: true,
        minWidth: '150px'
      });
    }
    
    if (columnVisibility.coordinates) {
      columns.push({
        id: 'coordinates',
        header: 'Coordinates',
        cell: (location) => editingLocation?.id === location.id ? (
          <div className="flex gap-1">
            <Input
              type="number"
              className="w-24"
              value={editingLocation?.coordinates?.latitude || 0}
              onChange={(e) => {
                if (!editingLocation) return;
                const lat = parseFloat(e.target.value);
                const lng = editingLocation.coordinates.longitude;
                setEditingLocation({
                  ...editingLocation, 
                  coordinates: new GeoPoint(lat, lng)
                });
              }}
              disabled={!canManage}
            />
            <Input
              type="number"
              className="w-24"
              value={editingLocation?.coordinates?.longitude || 0}
              onChange={(e) => {
                if (!editingLocation) return;
                const lng = parseFloat(e.target.value);
                const lat = editingLocation.coordinates.latitude;
                setEditingLocation({
                  ...editingLocation, 
                  coordinates: new GeoPoint(lat, lng)
                });
              }}
              disabled={!canManage}
            />
          </div>
        ) : `${location.coordinates.latitude.toFixed(4)}, ${location.coordinates.longitude.toFixed(4)}`,
        sortable: true,
        minWidth: '200px'
      });
    }
    
    if (columnVisibility.averageRating) {
      columns.push({
        id: 'averageRating',
        header: 'Avg Rating',
        cell: (location) => location.averageRating?.toFixed(1) ?? 'N/A',
        sortable: true,
        minWidth: '100px'
      });
    }
    
    if (columnVisibility.ratingCount) {
      columns.push({
        id: 'ratingCount',
        header: 'Ratings',
        cell: (location) => location.ratingCount ?? 0,
        sortable: true,
        minWidth: '100px'
      });
    }
    
    if (columnVisibility.drinkCount) {
      columns.push({
        id: 'drinkCount',
        header: 'Drinks',
        cell: (location) => location.drinkCount ?? 0,
        sortable: true,
        minWidth: '100px'
      });
    }
    
    if (columnVisibility.lastSightingDate) {
      columns.push({
        id: 'lastSightingDate',
        header: 'Last Sighting',
        cell: (location) => location.lastSightingDate ? (
          <div className="flex items-center gap-1 whitespace-nowrap">
            <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" /> 
            {(location.lastSightingDate as Timestamp).toDate().toLocaleDateString()}
          </div>
        ) : 'N/A',
        sortable: true,
        minWidth: '150px'
      });
    }
    
    if (columnVisibility.googlePlaceId) {
      columns.push({
        id: 'googlePlaceId',
        header: 'Google Place ID',
        cell: (location) => editingLocation?.id === location.id ? (
          <Input
            value={editingLocation?.googlePlaceId || ""}
            onChange={(e) => setEditingLocation({...editingLocation!, googlePlaceId: e.target.value})}
            disabled={!canManage}
          />
        ) : location.googlePlaceId || 'N/A',
        sortable: true,
        minWidth: '200px'
      });
    }
    
    if (canManage) {
      columns.push({
        id: 'actions',
        header: 'Actions',
        cell: (location) => editingLocation?.id === location.id ? (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleEditLocation} disabled={!canManage}>Save</Button>
            <Button variant="ghost" onClick={() => setEditingLocation(null)}>Cancel</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => setEditingLocation(location)}
              disabled={!canManage}
            >
              Edit
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteLocationAction(location)}
              disabled={!canManage || !location.id}
            >
              Delete
            </Button>
          </div>
        ),
        sortable: false,
        minWidth: '180px'
      });
    }
    
    return columns;
  }, [columnVisibility, editingLocation, canManage, handleEditLocation]);
  
  // Create sort config for the table
  const tableSortConfig: SortConfig<FirestoreLocation> = useMemo(() => ({
    column: sortColumn,
    direction: sortDirection
  }), [sortColumn, sortDirection]);
  
  // Empty message with filters applied
  const emptyMessage = useMemo(() => {
    let message = "No locations found";
    if (searchTerm) message += ` for "${searchTerm}"`;
    if (filterLocationType) message += ` of type "${filterLocationType}"`;
    return message + '.';
  }, [searchTerm, filterLocationType]);

  return (
    <>
      <Card className="h-full flex flex-col overflow-hidden">
        <CardHeader>
          <CardTitle>Store Locations ({locations?.length || 0})</CardTitle>
          <CardDescription>Manage store locations for soda sightings</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col overflow-hidden pb-6">
          <div className="flex justify-between items-start mb-4 gap-4">
            <div className="flex gap-2 items-end flex-wrap">
              <div>
                <Label htmlFor="locationTypeFilter">Filter by Type</Label>
                <Select 
                  value={filterLocationType || "all"} 
                  onValueChange={(value) => {
                    setFilterLocationType(value === "all" ? null : value as LocationType);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    <SelectItem value="restaurant">Restaurants</SelectItem>
                    <SelectItem value="shop">Shops</SelectItem>
                    <SelectItem value="brewery">Breweries</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="searchTerm">Filter by Name/Address</Label>
                <Input 
                  id="searchTerm"
                  placeholder="Search name or address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-[220px]"
                />
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="ml-2">
                    <Settings2 className="h-4 w-4" />
                    <span className="sr-only">Toggle columns</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {Object.keys(columnVisibility).map((key) => (
                    <DropdownMenuCheckboxItem
                      key={key}
                      className="capitalize"
                      checked={columnVisibility[key as keyof ColumnVisibilityState]}
                      onCheckedChange={(checked) =>
                        setColumnVisibility((prev) => ({ ...prev, [key]: checked }))
                      }
                      onSelect={(event) => event.preventDefault()}
                    >
                      {key.replace(/([A-Z])/g, ' $1')} {/* Add space before capitals for display */}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <div className="flex gap-2">
              {canManage && (
                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button disabled={!canManage}>Add New Location</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Add New Location</DialogTitle>
                      <DialogDescription>
                        Fill in the details for the new store location.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="name" className="text-right">Name</Label>
                        <Input 
                          id="name" 
                          value={newLocation.name || ''} 
                          onChange={(e) => setNewLocation({...newLocation, name: e.target.value})}
                          className="col-span-3"
                          disabled={!canManage}
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="address" className="text-right">Address</Label>
                        <Input 
                          id="address" 
                          value={newLocation.address || ''} 
                          onChange={(e) => setNewLocation({...newLocation, address: e.target.value})}
                          className="col-span-3"
                          disabled={!canManage}
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="locationType" className="text-right">Type</Label>
                        <Select 
                          value={newLocation.locationType || 'other'} 
                          onValueChange={(value) => {
                            setNewLocation({...newLocation, locationType: value as LocationType});
                          }}
                        >
                          <SelectTrigger className="w-full col-span-3">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="restaurant">Restaurant</SelectItem>
                            <SelectItem value="shop">Shop</SelectItem>
                            <SelectItem value="brewery">Brewery</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="latitude" className="text-right">Latitude</Label>
                        <Input 
                          id="latitude" 
                          type="number"
                          value={newLocation.coordinates ? newLocation.coordinates.latitude : 0} 
                          onChange={(e) => {
                            const lat = parseFloat(e.target.value);
                            const lng = newLocation.coordinates ? newLocation.coordinates.longitude : 0;
                            setNewLocation({
                              ...newLocation, 
                              coordinates: new GeoPoint(lat, lng)
                            });
                          }}
                          className="col-span-3"
                          disabled={!canManage}
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="longitude" className="text-right">Longitude</Label>
                        <Input 
                          id="longitude" 
                          type="number"
                          value={newLocation.coordinates ? newLocation.coordinates.longitude : 0} 
                          onChange={(e) => {
                            const lng = parseFloat(e.target.value);
                            const lat = newLocation.coordinates ? newLocation.coordinates.latitude : 0;
                            setNewLocation({
                              ...newLocation, 
                              coordinates: new GeoPoint(lat, lng)
                            });
                          }}
                          className="col-span-3"
                          disabled={!canManage}
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="url" className="text-right">URL</Label>
                        <Input 
                          id="url" 
                          value={newLocation.url || ''} 
                          onChange={(e) => setNewLocation({...newLocation, url: e.target.value})}
                          className="col-span-3"
                          disabled={!canManage}
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="googlePlaceId" className="text-right">Google Place ID</Label>
                        <Input 
                          id="googlePlaceId" 
                          value={newLocation.googlePlaceId || ''} 
                          onChange={(e) => setNewLocation({...newLocation, googlePlaceId: e.target.value})}
                          className="col-span-3"
                          disabled={!canManage}
                        />
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button onClick={handleAddLocation} disabled={!canManage}>Add Location</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
          
          {generalLoading && !processedLocations?.length ? (
            <div className="flex-1 flex items-center justify-center">Loading locations...</div>
          ) : (
            <ScrollableTable
              data={processedLocations}
              columns={tableColumns}
              sortConfig={tableSortConfig}
              onSort={handleSort}
              emptyMessage={emptyMessage}
              height="calc(100vh - 370px)"
            />
          )}
        </CardContent>
      </Card>

      {/* Deletion Dialog */}
      {(interactionState === 'confirming_deletion' || interactionState === 'assessing_deletion') && selectedEntity?.type === 'location' && (
        <DeleteDialog
          isOpen={true}
          onClose={handleDeletionDialogClose}
          entityType={selectedEntity.type as EditableEntityType}
          entityName={deletionImpact.report?.primaryDocument.name || selectedEntity.id || 'Location'}
          assessmentState={getDeletionAssessmentState()}
          impactReport={deletionImpact.report}
          deletionState={operationLoading === 'pending' ? 'loading' : operationError ? 'error' : 'idle'}
          error={deletionImpact.error || operationError}
          onConfirm={handleConfirmLocationDeletion}
        />
      )}
    </>
  );
}; 